import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, ChevronRight, ChevronDown, FileText, Rocket, ListChecks,
  ShieldAlert, Database, Building2, HelpCircle, Settings, Download,
  ExternalLink, X, Play, BookOpen
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  'rocket': Rocket,
  'list-checks': ListChecks,
  'shield-alert': ShieldAlert,
  'database': Database,
  'building-2': Building2,
  'help-circle': HelpCircle,
  'settings': Settings
};

const HelpCenterPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Load categories and articles
  useEffect(() => {
    loadData();
  }, []);

  // Handle URL params for deep linking
  useEffect(() => {
    const articleSlug = searchParams.get('article');
    if (articleSlug && articles.length > 0) {
      const article = articles.find(a => a.slug === articleSlug);
      if (article) {
        setSelectedArticle(article);
        setSelectedCategory(article.category_id);
        setExpandedCategories(prev => ({ ...prev, [article.category_id]: true }));
      }
    }
  }, [searchParams, articles]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      
      // Load categories
      const catRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories);
        
        // Expand first category by default
        if (catData.categories.length > 0) {
          setExpandedCategories({ [catData.categories[0].id]: true });
          setSelectedCategory(catData.categories[0].id);
        }
      }
      
      // Load all articles
      const artRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (artRes.ok) {
        const artData = await artRes.json();
        setArticles(artData.articles);
        
        // Select first article if none selected
        if (!selectedArticle && artData.articles.length > 0) {
          setSelectedArticle(artData.articles[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load help data:', error);
      toast.error('Failed to load help center');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/help/search?q=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results);
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  }, [searchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const selectArticle = (article) => {
    setSelectedArticle(article);
    setSelectedCategory(article.category_id);
    setSearchParams({ article: article.slug });
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const downloadRecruiterGuide = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/recruiter-guide/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DCS_ATS_Recruiter_Guide_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Recruiter Guide downloaded!');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getCategoryArticles = (categoryId) => {
    return articles.filter(a => a.category_id === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-slate-50">
      {/* Left Sidebar - Categories & Articles */}
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        {/* Search */}
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="help-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); setIsSearching(false); }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {isSearching && searchResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.slug}
                  onClick={() => selectArticle(result)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                >
                  <div className="font-medium text-sm text-slate-800">{result.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{result.category_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Category Tree */}
        <div className="flex-1 overflow-y-auto p-2">
          {categories.map((category) => {
            const Icon = CATEGORY_ICONS[category.icon] || FileText;
            const categoryArticles = getCategoryArticles(category.id);
            const isExpanded = expandedCategories[category.id];
            
            return (
              <div key={category.id} className="mb-1" data-testid={`help-category-${category.id}`}>
                <button
                  onClick={() => toggleCategory(category.id)}
                  data-testid={`help-category-btn-${category.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                    selectedCategory === category.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  )}
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{category.name}</span>
                </button>
                
                {isExpanded && (
                  <div className="ml-6 mt-1 space-y-1">
                    {categoryArticles.map((article) => (
                      <button
                        key={article.slug}
                        onClick={() => selectArticle(article)}
                        data-testid={`help-article-${article.slug}`}
                        className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                          selectedArticle?.slug === article.slug
                            ? 'bg-blue-100 text-blue-800 font-medium'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {article.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Download Button */}
        <div className="p-4 border-t border-slate-200">
          <Button
            onClick={downloadRecruiterGuide}
            disabled={downloadingPdf}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="download-recruiter-guide-btn"
          >
            {downloadingPdf ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download Recruiter Guide
              </>
            )}
          </Button>
          <p className="text-xs text-slate-500 mt-2 text-center">PDF with steps & screenshots</p>
        </div>
      </div>
      
      {/* Main Content - Article */}
      <div className="flex-1 overflow-y-auto">
        {selectedArticle ? (
          <div className="max-w-4xl mx-auto p-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <span>Help Center</span>
              <ChevronRight className="w-4 h-4" />
              <span>{selectedArticle.category_name}</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-800">{selectedArticle.title}</span>
            </div>
            
            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-3">{selectedArticle.title}</h1>
              <p className="text-lg text-slate-600">{selectedArticle.content}</p>
            </div>
            
            {/* Steps */}
            <div className="space-y-6">
              {selectedArticle.steps?.map((step, index) => (
                <Card key={index} className="overflow-hidden" id={`step-${step.step_number}`}>
                  <CardContent className="p-0">
                    <div className="flex items-start gap-4 p-5">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                        {step.step_number}
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-800 text-lg">{step.text}</p>
                      </div>
                    </div>
                    
                    {step.screenshot_path && (
                      <div className="border-t border-slate-100 bg-slate-50 p-4">
                        <div className="relative">
                          <img
                            src={`/help-images/${step.screenshot_path}`}
                            alt={`Step ${step.step_number}`}
                            className="rounded-lg border border-slate-200 shadow-sm max-w-full"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div 
                            className="hidden items-center justify-center h-48 bg-slate-100 rounded-lg border border-slate-200"
                            style={{ display: 'none' }}
                          >
                            <div className="text-center text-slate-400">
                              <FileText className="w-8 h-8 mx-auto mb-2" />
                              <p className="text-sm">Screenshot: {step.screenshot_path}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Admin Edit Button */}
            {isAdmin && (
              <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-amber-800">Admin Controls</h4>
                    <p className="text-sm text-amber-600">You can edit this article</p>
                  </div>
                  <Button
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => toast.info('Article editor coming soon')}
                  >
                    Edit Article
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <BookOpen className="w-16 h-16 mb-4" />
            <p className="text-lg">Select an article to read</p>
          </div>
        )}
      </div>
      
      {/* Right Sidebar - On This Page */}
      {selectedArticle && selectedArticle.steps?.length > 0 && (
        <div className="w-56 bg-white border-l border-slate-200 p-4 hidden xl:block">
          <h3 className="font-semibold text-slate-800 mb-3">On this page</h3>
          <div className="space-y-2">
            {selectedArticle.steps.map((step) => (
              <a
                key={step.step_number}
                href={`#step-${step.step_number}`}
                className="block text-sm text-slate-600 hover:text-blue-600 transition-colors"
              >
                {step.step_number}. {step.text.substring(0, 40)}...
              </a>
            ))}
          </div>
          
          {/* Related Page Link */}
          {selectedArticle.related_page && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <h4 className="font-medium text-slate-700 mb-2 text-sm">Related Page</h4>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => navigate(selectedArticle.related_page)}
              >
                <ExternalLink className="w-3 h-3 mr-2" />
                Open in App
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpCenterPage;
